import React, { FC } from 'react'
import { Redirect, Route, Switch } from 'react-router-dom'

import { routes } from 'constants/routes'
import { DynamicLoadingPage } from 'containers/DynamicLoadingPage'
import PrivateRoute from 'containers/PrivateRoute'

import NotFoundView from 'views/NotFoundView'

import { useBellNotifications } from 'modules/activity/operations'
import { useVersionSubscriptions } from 'modules/versioning/operations'
import {
  AcceptShareInviteByInviteTokenView,
  AcceptShareInviteByShareShortIdView,
} from 'modules/shares/AcceptShareInviteView/'
import { ShareRoutes } from 'modules/shares/routes'
import SettingsView from 'modules/user/views/SettingsView'
import { Updates } from 'modules/user/views/Updates'
import { useShareSubscriptionsWithUi } from 'modules/shares/operations'
import { PrivateWorkspaceRoute } from 'modules/workspace/containers'

import WorkspaceDocumentsView from 'modules/workspace/views/WorkspaceDocumentsView'
import WorkspaceSettingsView from 'modules/workspace/views/WorkspaceSettingsView'
import WorkspaceSubscribeView from 'modules/workspace/views/WorkspaceSubscribeView'
import PersonalAuthRoute from 'modules/sso/components/PersonalAuthRoute'
import DisableMFAView from 'modules/user/views/DisableMFAView'

import { TeamToWorkspaceRedirect } from 'modules/workspace/components/TeamToWorkspaceRedirect'
import { UserToWorkspaceRedirect } from 'modules/workspace/components/UserToWorkspaceRedirect'

import CheckYourInboxView from 'modules/user/views/CheckYourInbox'

import { CoreLayoutWrapper } from './CoreLayout.styles'

const AcceptWorkspaceInviteView = React.lazy(
  () => import('modules/workspace/views/AcceptWorkspaceInviteView')
)

const RedeemLicenseConfirmationView = React.lazy(
  () => import('modules/workspace/views/RedeemLicenseConfirmationView')
)

const CreateWorkspaceView = React.lazy(
  () => import('modules/workspace/views/CreateWorkspaceView')
)

const CreateWorkspaceDoneView = React.lazy(
  () => import('modules/workspace/views/CreateWorkspaceDoneView')
)

const CoreLayout: FC = () => {
  useBellNotifications()
  useVersionSubscriptions()
  useShareSubscriptionsWithUi()

  return (
    <CoreLayoutWrapper>
      <React.Suspense fallback={<DynamicLoadingPage />}>
        <Switch>
          {/* Really Old Legacy Redirect Routes */}
          <Redirect
            from={routes.TEAMS_LEGACY.template()}
            to={routes.TEAM.template()}
          />

          {/* Legacy Redirect Routes */}
          {/* Redirect from Team to the Workspace */}
          <PrivateRoute
            path={routes.TEAM.template()}
            component={TeamToWorkspaceRedirect}
          />

          {/* Redirect from Baby Cloud to the Personal-Workspace */}
          <PrivateRoute
            path={[
              routes.PERSONAL_PROJECT.template(),
              routes.DOCUMENTS.template(),
              routes.LIBRARIES.template(),
              routes.TRASH.template(),
              routes.PERSONAL_DOCUMENTS.template(),
            ]}
            component={UserToWorkspaceRedirect}
          />

          {/* Workspace Routes */}
          <PrivateRoute
            path={routes.WORKSPACE_INVITE.template()}
            component={AcceptWorkspaceInviteView}
          />

          <PersonalAuthRoute
            fallbackRouteIfUserOnlyHasSsoAuth={({ location }) => ({
              pathname: routes.SIGN_IN.create({}),
              state: { from: location },
            })}
            path={[
              routes.WORKSPACE_CREATE.template(),
              routes.WORKSPACE_CREATE_DONE.template(),
            ]}
            render={() => (
              <>
                <PrivateRoute
                  exact
                  path={routes.WORKSPACE_CREATE_DONE.template()}
                  render={({ match }) => (
                    <CreateWorkspaceDoneView
                      workspaceId={match.params.workspaceId!}
                    />
                  )}
                />
                <PrivateRoute
                  exact
                  path={routes.WORKSPACE_CREATE.template()}
                  redirectPathIfSignOut="sign-up"
                  component={CreateWorkspaceView}
                />
              </>
            )}
          />

          <PrivateRoute
            exact
            path={routes.WORKSPACE_LICENSE_REDEEM_CONFIRM.template()}
            component={RedeemLicenseConfirmationView}
          />

          <PrivateWorkspaceRoute
            path={routes.WORKSPACE_SETTINGS.template()}
            component={WorkspaceSettingsView}
          />

          <PrivateWorkspaceRoute
            path={routes.WORKSPACE_SUBSCRIBE.template()}
            component={WorkspaceSubscribeView}
          />

          <PrivateWorkspaceRoute
            exact
            path={[
              routes.WORKSPACE_PROJECT.template(),
              routes.WORKSPACE_SHARES.template(),
              routes.WORKSPACE_LIBRARIES.template(),
              routes.WORKSPACE_TRASH.template(),
              routes.WORKSPACE_DRAFTS.template(),
              routes.WORKSPACE_SHARED_WITH_ME.template(),
              routes.WORKSPACE_TRASH_PROJECT.template(),
            ]}
            component={WorkspaceDocumentsView}
          />

          <PrivateRoute
            exact
            path={routes.UPDATES.template()}
            component={Updates}
          />

          {/* Shares */}
          <Route
            path={routes.SHARE_INVITE.template()}
            component={AcceptShareInviteByShareShortIdView}
          />

          <Route
            path={routes.SHARE_ACCEPT_INVITE_ROOT.template()}
            component={AcceptShareInviteByInviteTokenView}
          />

          <Route path={routes.SHARE_VIEW.template()} component={ShareRoutes} />

          <PrivateRoute
            exact
            path={routes.CHECK_YOUR_INBOX.template()}
            component={CheckYourInboxView}
          />

          {/* User */}
          <PersonalAuthRoute
            exact
            path={[
              routes.PERSONAL_SETTINGS.template(),
              routes.PERSONAL_SETTINGS_UNSUBSCRIBE.template(),
              routes.PERSONAL_SETTINGS_API_KEYS.template(),
              routes.PERSONAL_SETTINGS_CONFIRM_DISABLE_MFA.template(),
            ]}>
            <PrivateRoute component={SettingsView} />
          </PersonalAuthRoute>
          <PersonalAuthRoute
            exact
            path={[routes.PERSONAL_SETTINGS_DISABLE_MFA.template()]}>
            <PrivateRoute component={DisableMFAView} />
          </PersonalAuthRoute>

          {/* Not found default */}
          <Route component={NotFoundView} />
        </Switch>
      </React.Suspense>
    </CoreLayoutWrapper>
  )
}

export default CoreLayout
